/* ============================================================
   LEFTOVRZ — predictive search

   Talks to Shopify's own /search/suggest.json, so the results are whatever
   the storefront would really return; nothing about the catalogue is
   duplicated here.

   The input is a combobox: arrow keys move through the panel, Enter opens
   the highlighted product or submits the raw query, Escape closes. That is
   the difference between a dropdown and a listbox a keyboard can reach.
   ============================================================ */
(() => {
  'use strict';
  if (window.__lvSearch) return;
  window.__lvSearch = true;

  const DEBOUNCE = 180;
  const LIMIT = 6;
  /* One character, not two. "Like any other search bar" means the panel
     answers the first thing you type; suggest.json is happy to match on a
     single letter and the debounce keeps it from being chatty. */
  const MIN = 1;

  /* suggest.json hands back a bare amount ("6500.00"), not a formatted
     price, so it gets wrapped in the shop's own money format rather than
     being printed as a naked number. */
  const withCurrency = (amount) => {
    const fmt = (window.LEFTOVRZ_SHOP && window.LEFTOVRZ_SHOP.moneyFormat) || '';
    if (!amount) return '';
    return /\{\{\s*amount/.test(fmt)
      ? fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]*>/g, '')
      : String(amount);
  };

  const esc = (t) => String(t == null ? '' : t)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  for (const root of document.querySelectorAll('[data-search]')) {
    const form = root.querySelector('form');
    const input = root.querySelector('.navsearch__input');
    const panel = root.querySelector('.navsearch__panel');
    if (!form || !input || !panel) continue;

    let timer = 0, controller = null, items = [], cursor = -1;

    const close = () => {
      panel.hidden = true;
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      items = []; cursor = -1;
    };

    const mark = () => {
      items.forEach((el, i) => {
        el.classList.toggle('is-on', i === cursor);
        if (i === cursor) {
          el.setAttribute('aria-selected', 'true');
          el.scrollIntoView({ block: 'nearest' });
        } else el.removeAttribute('aria-selected');
      });
    };

    const render = (products, term) => {
      if (!products.length) {
        panel.innerHTML =
          `<p class="navsearch__none mono">No match for &ldquo;${esc(term)}&rdquo;</p>`;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        items = []; cursor = -1;
        return;
      }
      panel.innerHTML = products.map((p) => {
        const img = p.featured_image && p.featured_image.url;
        const price = withCurrency(p.price);
        return `<a class="navsearch__hit" role="option" href="${esc(p.url)}">
          <span class="navsearch__thumb">${img ? `<img src="${esc(img)}" alt="" loading="lazy">` : ''}</span>
          <span class="navsearch__meta">
            <span class="navsearch__title">${esc(p.title)}</span>
            ${price ? `<span class="navsearch__price mono">${esc(price)}</span>` : ''}
          </span>
        </a>`;
      }).join('') +
      `<a class="navsearch__all mono" role="option" href="${esc(form.action)}?q=${encodeURIComponent(term)}&type=product">
         See everything for &ldquo;${esc(term)}&rdquo; &rarr;</a>`;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      items = [...panel.querySelectorAll('[role="option"]')];
      cursor = -1;
    };

    /* Answers are kept for the page's life: backspacing, refocusing and
       retyping a term are served locally instead of re-asking suggest.json. */
    const seen = new Map();
    const ask = (term) => {
      if (seen.has(term)) { render(seen.get(term), term); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = `/search/suggest.json?q=${encodeURIComponent(term)}` +
                  `&resources[type]=product&resources[limit]=${LIMIT}` +
                  `&resources[options][unavailable_products]=last`;
      fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => {
          const products = (d && d.resources && d.resources.results &&
                            d.resources.results.products) || [];
          if (seen.size > 60) seen.clear();
          seen.set(term, products);
          // the field may have moved on while this was in flight
          if (input.value.trim() === term) render(products, term);
        })
        .catch((e) => { if (e && e.name !== 'AbortError') close(); });
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      clearTimeout(timer);
      if (term.length < MIN) { close(); return; }
      timer = setTimeout(() => ask(term), DEBOUNCE);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); input.blur(); return; }
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); cursor = (cursor + 1) % items.length; mark(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); cursor = (cursor - 1 + items.length) % items.length; mark(); }
      else if (e.key === 'Enter' && cursor >= 0) { e.preventDefault(); items[cursor].click(); }
    });

    // A click inside the panel is a navigation, not a dismissal.
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) close();
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN) ask(input.value.trim());
    });

    /* ---- the phone's version: the field is summoned, not always there ---- */
    /* Delegated, and on the document: the magnifier in the header is no
       longer the only thing that opens this — the footer has a Search row
       too, and it is rendered far away from here. A direct binding to one
       element would have quietly worked for the header and done nothing for
       the footer. */
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-search-open]');
      if (!t) return;
      e.preventDefault();
      root.classList.add('is-open');
      /* The bar is fixed, so there is nothing to scroll to — but a footer
         press should still land the caret in the field. */
      input.focus({ preventScroll: false });
      input.select();
    });

    const openBtn = null;
    const closeBtn = root.querySelector('[data-search-close]');

    if (closeBtn) closeBtn.addEventListener('click', () => {
      root.classList.remove('is-open');
      close();
      input.value = '';
    });
  }
})();
